import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const MessagingInterface = ({ messages, onRefresh, expanded = false }) => {
  const [showCompose, setShowCompose] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [selectedThread, setSelectedThread] = useState(null);

  const handleSendMessage = () => {
    if (!newMessage?.trim()) return;
    
    // In a real implementation, this would send the message to Supabase
    console.log('Sending message:', newMessage);
    setNewMessage('');
    setShowCompose(false);
    
    if (onRefresh) {
      onRefresh();
    }
  };

  const getMessageIcon = (type) => {
    switch (type) {
      case 'system': return 'Bell';
      case 'support': return 'HelpCircle';
      case 'account_manager': return 'UserCheck';
      default: return 'MessageSquare';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString();
  };

  return (
    <div className="glass-container rounded-xl border border-border/50 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center">
            <Icon name="MessageSquare" size={24} className="text-accent" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Messages</h2>
            <p className="text-sm text-muted-foreground">
              {messages?.length || 0} conversations
            </p>
          </div>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          size="sm"
          onClick={() => setShowCompose(!showCompose)}
        >
          New Message
        </Button>
      </div>
      {/* Compose Message */}
      {showCompose && (
        <div className="mb-6 p-4 rounded-lg border border-border/50 bg-accent/5">
          <h3 className="text-sm font-semibold text-foreground mb-3">Compose New Message</h3>
          <div className="space-y-3">
            <select className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
              <option value="support">Support Team</option>
              <option value="account_manager">Account Manager</option>
              <option value="compliance">Compliance Team</option>
            </select>
            <Input
              type="text"
              placeholder="Subject"
              className="w-full"
            />
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e?.target?.value)}
              placeholder="Type your message here..."
              rows={4}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
            <div className="flex items-center justify-end space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCompose(false)}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="Send"
                onClick={handleSendMessage}
                disabled={!newMessage?.trim()}
              >
                Send Message
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Messages List */}
      <div className="space-y-3">
        {messages?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No messages yet</p>
            <p className="text-sm text-muted-foreground mt-2">
              Start a conversation with your account manager or support team
            </p>
          </div>
        ) : (
          messages?.slice(0, expanded ? undefined : 5)?.map((message) => (
            <div
              key={message?.id}
              className="p-4 rounded-lg border border-border/50 hover:bg-accent/5 transition-colors cursor-pointer"
              onClick={() => setSelectedThread(message?.id)}
            >
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                  <Icon name={getMessageIcon(message?.message_type)} size={18} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold text-foreground truncate">
                      {message?.collaboration_threads?.title || 'Untitled Thread'}
                    </h4>
                    <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                      {formatTimestamp(message?.sent_at)}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {message?.content || 'No content'}
                  </p>
                  {message?.attachments_count > 0 && (
                    <div className="flex items-center space-x-1 mt-2">
                      <Icon name="Paperclip" size={14} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        {message?.attachments_count} attachment{message?.attachments_count > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {!expanded && messages?.length > 5 && (
        <div className="mt-4 text-center">
          <Button variant="ghost" iconName="ChevronDown" size="sm">
            View All Messages ({messages?.length})
          </Button>
        </div>
      )}
    </div>
  );
};

export default MessagingInterface;